import { ExistingItemHandling, type BasketItem } from '@scayle/storefront-nuxt'
import { getVariantIds, isBuyXGetYType } from '~/utils/promotion'

let giftRemovalInProgress = false

export const useFreeGiftPromotion = (setup: boolean) => {
  const log = useAgsLog('useFreeGiftPromotion')
  const giftManuallyRemoved = useSessionStorage('no-gift', () => false)
  const basket = useAgsBasket()

  if (setup) {
    watch(basket.data, (newBasket, oldBasket) => {
      const oldItems = [...(oldBasket?.items || [])]
      const newItems = [...(newBasket?.items || [])]

      if (newItems.length && oldItems.length && !giftRemovalInProgress) {
        const removedItems = oldItems.filter(
          (oi) => !newItems.some((ni) => ni.key === oi.key),
        )
        const manuallyRemoved = removedItems.some(isGiftProduct)
        if (manuallyRemoved) {
          giftManuallyRemoved.value = true
          log.info('gift-manually-removed')
        }
      }

      if (giftManuallyRemoved.value) {
        log.info('gift-manually-removed')
      } else {
        const freeGift = freeGiftBasketItem.value
        const hasPromotion = Boolean(freeGift?.promotion)
        log.info('gift-check', {
          freeGift,
          hasPromotion,
        })

        if (freeGift && (!hasPromotion || newItems.length === 1)) {
          removeFreeGiftFromBasket()
        }
      }
    })
  }

  const freeGiftBasketItem = computed(() =>
    basket.data.value?.items.find(isGiftProduct),
  )

  const freeGiftPromotion = computed(() => freeGiftBasketItem.value?.promotion)

  function isGiftProduct(item: BasketItem) {
    return (item.customData as any)?.freeGift
  }

  const isGiftInBasket = computed(() => Boolean(freeGiftBasketItem.value))

  const giftVariantIds = computed(() =>
    freeGiftPromotion.value ? getVariantIds(freeGiftPromotion.value) : [],
  )

  const addFreeGiftToBasket = async (promotion: Promotion) => {
    log.info('add-gift', {
      promotion,
    })

    try {
      for (const variantId of getVariantIds(promotion)) {
        await basket.addItem({
          variantId: variantId,
          quantity: 1,
          customData: { freeGift: true },
          promotionId: promotion?.id,
          existingItemHandling: ExistingItemHandling.REPLACE_EXISTING,
        })
        return { success: true }
      }
    } catch (error) {
      log.error('Could not add free gift to basket:', error)
      log.info('add-gift-error', { error })
      return {
        success: false,
        reason: 'Error adding gift to basket',
        extra: { error },
      }
    }
  }

  const removeFreeGiftFromBasket = async () => {
    log.info('remove-gift', {
      freeGift: freeGiftBasketItem.value,
    })
    giftRemovalInProgress = true
    try {
      if (freeGiftBasketItem.value) {
        await basket.removeFreeGiftItem(freeGiftBasketItem.value.key)
      }
    } catch (error) {
      log.error('Could not remove free gift from basket:', error)
      log.info('remove-gift-error', { error })
    } finally {
      giftRemovalInProgress = false
    }
  }

  return {
    isGiftInBasket,
    isGiftProduct,
    giftVariantIds,
    tryApply: async (promotion: Promotion) => {
      if (!isBuyXGetYType(promotion)) return { success: false }

      if (giftRemovalInProgress) {
        return { success: false, reason: 'Gift removal in progress' }
      }
      if (giftManuallyRemoved.value) {
        return { success: false, reason: 'Gift manually removed' }
      }

      return await addFreeGiftToBasket(promotion)
    },
  }
}
